import { useUserContext } from '@/contexts/UserContext';
import { VariantOriginResult } from '@/modals/VariantOriginModal/types';
import {
  AgentRunAttachment,
  AssistantAgentSession,
  Constraints,
  VariantPrompt,
} from '@superfunnel/types';
import { httpsCallable } from 'firebase/functions';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAnalytics } from '../../contexts/AnalyticsContext';
import { useSnackbarContext } from '../../contexts/SnackbarContext';
import { functions } from '../../Firebase';
import { copyWebsiteDevelopment } from '../../services/DynamicPagesService';
import { EditorModalState } from './useAiAssistantLogic';
import { UseAssistantAgentSessionReturn } from './useAssistantAgentSession';

/**
 * State for the edit variant name/description/path modal
 */
export interface EditVariantModalState {
  variantId: string;
  name: string;
  description: string;
  path: string;
}

/**
 * State for the edit variant constraints modal
 */
export interface EditConstraintsModalState {
  variantId: string;
  constraints: Constraints;
}

/**
 * State for the delete variant confirmation modal
 */
export interface DeleteVariantModalState {
  variantId: string;
  variantName: string;
}

/**
 * State for the variant origin modal (shown before generating)
 */
export interface VariantOriginModalState {
  /** The variant ID that will be generated */
  variantId: string;
  /** Default prompt to prefill the modal (from the variant's stored prompt) */
  defaultPrompt?: VariantPrompt;
}

export interface UseDraftCardActionsProps {
  /** The selected account ID */
  accountId: string | undefined;
  /** The assistant session data and methods */
  assistantSession: UseAssistantAgentSessionReturn;
  /** Chat messages for extracting initial message content */
  messages: Array<{
    type: string;
    content?: string;
    attachments?: unknown[];
  }>;
  /** Callback to open the editor modal */
  openEditorModal: (state: EditorModalState) => void;
}

export interface UseDraftCardActionsReturn {
  /** Callback when generate is clicked for a variant */
  handleGenerateVariant: (
    variantId: string,
    originData?: VariantOriginResult,
  ) => Promise<void>;
  /** Callback when edit (branch/fork) is clicked for a variant */
  handleEditVariant: (variantId: string) => void;
  /** Callback when edit constraints (smart traffic) is clicked for a variant */
  handleEditConstraints: (variantId: string) => void;
  /** Callback when delete is clicked for a variant */
  handleDeleteVariant: (variantId: string) => Promise<void>;
  /** Callback when settings is clicked */
  handleSettings: () => void;
  /** Callback when "Go to dynamic page" is clicked */
  handleGoToPage: () => void;
  /** ID of the variant currently being generated */
  generatingVariantId: string | null;

  // Settings modal
  /** Whether the settings modal is open */
  settingsModalOpen: boolean;
  /** Close the settings modal */
  closeSettingsModal: () => void;
  /** Update page data (for settings modal) */
  handleUpdatePage: (data: any, mergeFields?: string[]) => Promise<void>;
  /** Update metadata (for settings modal) */
  handleUpdateMetadata: (metadata: {
    path?: string;
    alternatePathes?: string[];
  }) => Promise<void>;

  // Edit variant modal
  /** State for the edit variant modal */
  editVariantModal: EditVariantModalState | undefined;
  /** Close the edit variant modal */
  closeEditVariantModal: () => void;
  /** Save changes from the edit variant modal */
  saveEditVariantModal: (
    name: string,
    description: string,
    path: string | null,
  ) => Promise<void>;

  // Edit constraints modal
  /** State for the edit constraints modal */
  editConstraintsModal: EditConstraintsModalState | undefined;
  /** Close the edit constraints modal */
  closeEditConstraintsModal: () => void;
  /** Save changes from the edit constraints modal */
  saveEditConstraintsModal: (constraints: Constraints) => Promise<void>;

  // Delete variant modal
  /** State for the delete variant confirmation modal */
  deleteVariantModal: DeleteVariantModalState | undefined;
  /** Close the delete variant modal */
  closeDeleteVariantModal: () => void;
  /** Confirm deletion of the variant */
  confirmDeleteVariant: () => Promise<void>;

  // Variant origin modal (shown before generating)
  /** State for the variant origin modal */
  variantOriginModal: VariantOriginModalState | undefined;
  /** Open the variant origin modal for a specific variant */
  openVariantOriginModal: (
    variantId: string,
    defaultPrompt?: VariantPrompt,
  ) => void;
  /** Close the variant origin modal */
  closeVariantOriginModal: () => void;
  /** Handle selection from the variant origin modal and proceed with generation */
  handleVariantOriginSelect: (result: VariantOriginResult) => Promise<void>;
}

/**
 * Hook for DynamicPageDraftCard actions
 * Handles:
 * - Generating variants (creating DynamicPage if needed, opening editor)
 * - Editing variants
 * - Deleting variants
 * - Settings navigation
 * - Go to page navigation
 */
export function useDraftCardActions({
  accountId,
  assistantSession,
  messages,
  openEditorModal,
}: UseDraftCardActionsProps): UseDraftCardActionsReturn {
  const { track } = useAnalytics();
  const { showError } = useSnackbarContext();
  const { selectedAccount } = useUserContext();
  const navigate = useNavigate();

  // Generating variant state
  const [generatingVariantId, setGeneratingVariantId] = useState<string | null>(
    null,
  );

  // Edit variant modal state
  const [editVariantModal, setEditVariantModal] = useState<
    EditVariantModalState | undefined
  >(undefined);

  // Edit constraints modal state
  const [editConstraintsModal, setEditConstraintsModal] = useState<
    EditConstraintsModalState | undefined
  >(undefined);

  // Delete variant modal state
  const [deleteVariantModal, setDeleteVariantModal] = useState<
    DeleteVariantModalState | undefined
  >(undefined);

  // Variant origin modal state (shown before generating)
  const [variantOriginModal, setVariantOriginModal] = useState<
    VariantOriginModalState | undefined
  >(undefined);

  // Settings modal state
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);

  /**
   * Build the dynamic page payload from structure data
   */
  const buildDynamicPagePayload = useCallback(
    (
      dpData: NonNullable<
        AssistantAgentSession['structureData']
      >['dynamicPage'],
    ) => {
      const payload: {
        name: string;
        path: string;
        goal: string;
        audience: string;
        description: string;
        formId?: string;
        link?: string;
        calendlyLink?: string;
        appStoreLink?: string;
        playStoreLink?: string;
        language?: { name: string; code: string; rtl: boolean };
        domains?: string[];
        alternatePathes?: string[];
      } = {
        name: dpData.name || 'Untitled Page',
        path: dpData.path || `page-${Date.now()}`,
        goal: dpData.goal || 'leads',
        audience: dpData.audience || 'General audience',
        description: dpData.description || 'Created by AI Assistant',
      };

      // Add goal-specific fields based on goal type
      if ('formId' in dpData && dpData.formId) {
        payload.formId = dpData.formId;
      }
      if ('link' in dpData && dpData.link) {
        payload.link = dpData.link;
      }
      if ('calendlyLink' in dpData && dpData.calendlyLink) {
        payload.calendlyLink = dpData.calendlyLink;
      }
      if ('appStoreLink' in dpData && dpData.appStoreLink) {
        payload.appStoreLink = dpData.appStoreLink;
      }
      if ('playStoreLink' in dpData && dpData.playStoreLink) {
        payload.playStoreLink = dpData.playStoreLink;
      }
      if (dpData.language) {
        payload.language = dpData.language;
      }
      if (dpData.domains) {
        payload.domains = dpData.domains;
      }
      if (dpData.alternatePathes) {
        payload.alternatePathes = dpData.alternatePathes;
      }

      return payload;
    },
    [],
  );

  /**
   * Extract the first user message and all file attachments from chat messages
   */
  const getInitialMessageData = useCallback(() => {
    const firstUserMessage = messages.find(
      (msg) => msg.type === 'user',
    )?.content;

    const allAttachments = messages
      .flatMap((msg) => (Array.isArray(msg.attachments) ? msg.attachments : []))
      .filter(
        (att): att is AgentRunAttachment =>
          att !== null &&
          att !== undefined &&
          typeof att === 'object' &&
          'type' in att &&
          att.type === 'file',
      );

    return {
      content: 'Create a fully working page, user input:\n' + firstUserMessage,
      attachments: allAttachments,
    };
  }, [messages]);

  /**
   * Open the editor modal for a variant
   * @param variantId - The variant ID to edit
   * @param dynamicPageId - The dynamic page ID
   * @param websiteId - The website ID
   * @param originData - Optional origin data for copying from source
   */
  const openVariantEditor = useCallback(
    (
      variantId: string,
      dynamicPageId: string,
      websiteId: string,
      originData?: VariantOriginResult,
    ) => {
      const baseMessage = getInitialMessageData();

      // Merge attachments from base message and originData
      const mergedAttachments = originData?.attachments?.length
        ? originData?.attachments
        : baseMessage.attachments;

      // Build the initial message based on origin type
      let initialMessage: EditorModalState['initialMessage'] = baseMessage;
      if (originData) {
        const promptSuffix = originData.prompt
          ? `\n\nAdditional instructions: ${originData.prompt}`
          : '';

        switch (originData.originType) {
          case 'empty':
            initialMessage = {
              content: `Create a fully working page from scratch.${promptSuffix}`,
              attachments:
                mergedAttachments.length > 0 ? mergedAttachments : undefined,
            };
            break;
          case 'existingVariant':
            if (originData.selectedVariant) {
              initialMessage = {
                content: `Copy the content from variant "${originData.selectedVariant.variantName}" (ID: ${originData.selectedVariant.variantId}) and use it as a starting point.${promptSuffix}`,
                attachments:
                  mergedAttachments.length > 0 ? mergedAttachments : undefined,
              };
            }
            break;
          case 'differentDynamicPage':
            if (originData.selectedVariant) {
              initialMessage = {
                content: `Copy the content from variant "${originData.selectedVariant.variantName}" from page "${originData.selectedVariant.dynamicPageName}" (variant ID: ${originData.selectedVariant.variantId}, page ID: ${originData.selectedVariant.dynamicPageId}) and use it as a starting point.${promptSuffix}`,
                attachments:
                  mergedAttachments.length > 0 ? mergedAttachments : undefined,
              };
            }
            break;
          case 'controlVariation':
            if (originData.selectedVariant) {
              initialMessage = {
                content: `Start from the control variation "${originData.selectedVariant.variantName}" (ID: ${originData.selectedVariant.variantId}) and use it as a starting point.${promptSuffix}`,
                attachments:
                  mergedAttachments.length > 0 ? mergedAttachments : undefined,
              };
            }
            break;
        }
      }

      openEditorModal({
        websiteId,
        variantId,
        dynamicPageId,
        initialMessage,
      });

      track('ai_assistant_variant_editor_opened', {
        variant_id: variantId,
        dynamic_page_id: dynamicPageId,
        origin_type: originData?.originType,
        website_id: websiteId,
      });
    },
    [getInitialMessageData, openEditorModal, track],
  );

  /**
   * Create a new DynamicPage from structure data
   * @param generatingVariantId - The ID of the variant being generated (to mark as 'generated')
   */
  const createDynamicPage = useCallback(
    async (
      structureData: NonNullable<AssistantAgentSession['structureData']>,
      updateStructureData: UseAssistantAgentSessionReturn['updateStructureData'],
      generatingVariantId: string,
    ): Promise<{
      dynamicPageId: string;
      variants: Array<{ id: string; name: string; websiteId: string }>;
    }> => {
      console.log(
        '[useDraftCardActions] Creating DynamicPage from structure data',
      );

      const upsertDynamicPageFn = httpsCallable<
        {
          accountId: string;
          dynamicPage: ReturnType<typeof buildDynamicPagePayload>;
          variants: Array<{
            id?: string;
            name: string;
            description: string;
            constraints?: NonNullable<
              AssistantAgentSession['structureData']
            >['variants'][0]['constraints'];
          }>;
        },
        {
          dynamicPageId: string;
          variants: Array<{ id: string; name: string; websiteId: string }>;
        }
      >(functions, 'accounts/campaigns/upsertDynamicPage');

      const dynamicPagePayload = buildDynamicPagePayload(
        structureData.dynamicPage,
      );

      const result = await upsertDynamicPageFn({
        accountId: accountId!,
        dynamicPage: dynamicPagePayload,
        variants: structureData.variants.map((v) => ({
          id: v.id,
          name: v.name || 'Untitled Variant',
          description: v.description || '',
          constraints: v.constraints,
        })),
      });

      const { dynamicPageId, variants: createdVariants } = result.data;
      console.log('[useDraftCardActions] DynamicPage created:', dynamicPageId);

      // Build a map of variant IDs to their created websiteIds
      const variantWebsiteMap = new Map(
        createdVariants.map((v) => [v.id, v.websiteId]),
      );

      // Update structure data with saved status, created DP ID, variant websiteIds, and mark generating variant
      await updateStructureData({
        status: 'saved',
        createdDynamicPageId: dynamicPageId,
        variants: structureData.variants.map((v) => ({
          ...v,
          createdWebsiteId: variantWebsiteMap.get(v.id) ?? v.createdWebsiteId,
          // Mark the variant being generated as 'generated'
          ...(v.id === generatingVariantId
            ? { status: 'generated' as const }
            : {}),
        })),
      });

      track('ai_assistant_dynamic_page_created', {
        dynamic_page_id: dynamicPageId,
        variant_count: structureData.variants.length,
      });

      return { dynamicPageId, variants: createdVariants };
    },
    [accountId, buildDynamicPagePayload, track],
  );

  /**
   * Generate a variant - creates the DynamicPage (if not already created) and opens editor
   * @param variantId - The variant ID to generate
   * @param originData - Optional origin data for copying from source variant/page
   */
  const handleGenerateVariant = useCallback(
    async (variantId: string, originData?: VariantOriginResult) => {
      if (!accountId || !assistantSession.structureData) {
        console.error(
          '[useDraftCardActions] Cannot generate variant: missing account or structure data',
        );
        return;
      }

      const { structureData, updateStructureData } = assistantSession;
      const variant = structureData.variants.find((v) => v.id === variantId);

      if (!variant) {
        console.error(
          `[useDraftCardActions] Variant ${variantId} not found in structure data`,
        );
        return;
      }

      setGeneratingVariantId(variantId);
      track('ai_assistant_generate_variant_started', {
        variant_id: variantId,
        variant_name: variant.name,
        is_structure_saved: structureData.status === 'saved',
      });

      try {
        let dynamicPageId: string | undefined;
        let websiteId = '';

        if (structureData.status !== 'saved') {
          // Create DynamicPage and open editor immediately
          const result = await createDynamicPage(
            structureData,
            updateStructureData,
            variantId,
          );
          dynamicPageId = result.dynamicPageId;

          // Find the websiteId for the requested variant from the API response
          const createdVariant = result.variants.find(
            (v) => v.id === variantId,
          );
          websiteId = createdVariant?.websiteId ?? '';
        } else {
          // Use existing dynamic page ID
          dynamicPageId =
            structureData.createdDynamicPageId ||
            assistantSession.agentSessionData?.dynamicPageId;

          // Use existing websiteId if variant was already generated
          websiteId = variant.createdWebsiteId ?? '';
        }

        if (dynamicPageId) {
          // If copying from another variant/page, copy the development data first
          if (
            originData &&
            originData.originType !== 'empty' &&
            originData.selectedVariant &&
            websiteId
          ) {
            const { selectedVariant } = originData;
            try {
              console.log(
                '[useDraftCardActions] Copying development data from source',
                {
                  source: selectedVariant,
                  targetWebsiteId: websiteId,
                },
              );
              await copyWebsiteDevelopment(
                accountId,
                selectedVariant.dynamicPageId,
                selectedVariant.websiteId,
                dynamicPageId,
                websiteId,
              );
              track('ai_assistant_development_copied', {
                variant_id: variantId,
                source_dynamic_page_id: selectedVariant.dynamicPageId,
                source_website_id: selectedVariant.websiteId,
                target_dynamic_page_id: dynamicPageId,
                target_website_id: websiteId,
              });
            } catch (copyError) {
              console.error(
                '[useDraftCardActions] Failed to copy development data:',
                copyError,
              );
              // Continue with opening the editor even if copy fails
              // The user can still work with an empty page
            }
          }

          openVariantEditor(variantId, dynamicPageId, websiteId, originData);

          // Mark the variant as generated (generation initiated)
          // Only update if page was already saved - for new pages, createDynamicPage handles the update
          if (structureData.status === 'saved') {
            const updatedVariants = structureData.variants.map((v) =>
              v.id === variantId ? { ...v, status: 'generated' as const } : v,
            );
            await updateStructureData({ variants: updatedVariants });
          }
        }
      } catch (error) {
        console.error('[useDraftCardActions] Error generating variant:', error);
        track('ai_assistant_generate_variant_failed', {
          variant_id: variantId,
          error: error instanceof Error ? error.message : 'Unknown error',
        });
        showError(error);
      } finally {
        setGeneratingVariantId(null);
      }
    },
    [
      accountId,
      assistantSession,
      createDynamicPage,
      openVariantEditor,
      showError,
      track,
    ],
  );

  /**
   * Edit a variant - opens the edit variant name/description modal
   */
  const handleEditVariant = useCallback(
    (variantId: string) => {
      const variant = assistantSession.structureData?.variants.find(
        (v) => v.id === variantId,
      );

      if (!variant) {
        console.error(
          `[useDraftCardActions] Variant ${variantId} not found in structure data`,
        );
        return;
      }

      track('ai_assistant_edit_variant_clicked', {
        variant_id: variantId,
        variant_name: variant.name,
        is_generated: variant.status === 'generated',
      });

      // Open the edit variant modal
      setEditVariantModal({
        variantId,
        name: variant.name || '',
        description: variant.description || '',
        path: variant.path || '',
      });
    },
    [assistantSession.structureData?.variants, track],
  );

  /**
   * Close the edit variant modal
   */
  const closeEditVariantModal = useCallback(() => {
    setEditVariantModal(undefined);
  }, []);

  /**
   * Save changes from the edit variant modal
   */
  const saveEditVariantModal = useCallback(
    async (name: string, description: string, path: string | null) => {
      if (!editVariantModal || !assistantSession.structureData) return;

      const { variantId } = editVariantModal;

      track('ai_assistant_edit_variant_saved', {
        variant_id: variantId,
        name_changed: name !== editVariantModal.name,
        description_changed: description !== editVariantModal.description,
        path_changed: (path || '') !== editVariantModal.path,
      });

      // Update the variant in structure data
      const updatedVariants = assistantSession.structureData.variants.map(
        (v) =>
          v.id === variantId
            ? { ...v, name, description, path: path || undefined }
            : v,
      );

      await assistantSession.updateStructureData({
        variants: updatedVariants,
      });

      // Close the modal
      setEditVariantModal(undefined);
    },
    [editVariantModal, assistantSession, track],
  );

  /**
   * Edit variant constraints (Smart Traffic settings) - opens the constraints modal
   */
  const handleEditConstraints = useCallback(
    (variantId: string) => {
      const variant = assistantSession.structureData?.variants.find(
        (v) => v.id === variantId,
      );

      if (!variant) {
        console.error(
          `[useDraftCardActions] Variant ${variantId} not found in structure data`,
        );
        return;
      }

      track('ai_assistant_edit_constraints_clicked', {
        variant_id: variantId,
        variant_name: variant.name,
        has_constraints: !!variant.constraints,
      });

      // Open the constraints modal
      setEditConstraintsModal({
        variantId,
        constraints: variant.constraints || {},
      });
    },
    [assistantSession.structureData?.variants, track],
  );

  /**
   * Close the edit constraints modal
   */
  const closeEditConstraintsModal = useCallback(() => {
    setEditConstraintsModal(undefined);
  }, []);

  /**
   * Save changes from the edit constraints modal
   */
  const saveEditConstraintsModal = useCallback(
    async (constraints: Constraints) => {
      if (!editConstraintsModal || !assistantSession.structureData) return;

      const { variantId } = editConstraintsModal;

      track('ai_assistant_edit_constraints_saved', {
        variant_id: variantId,
        has_keywords: !!constraints.keywords?.length,
        has_devices: !!constraints.devices?.length,
        has_campaigns: !!constraints.campaigns?.length,
        has_ad_groups: !!constraints.adGroups?.length,
        has_ads: !!constraints.ads?.length,
        has_schedules: !!constraints.schedules?.length,
      });

      // Update the variant constraints in structure data
      const updatedVariants = assistantSession.structureData.variants.map(
        (v) => (v.id === variantId ? { ...v, constraints } : v),
      );

      await assistantSession.updateStructureData({
        variants: updatedVariants,
      });

      // Close the modal
      setEditConstraintsModal(undefined);
    },
    [editConstraintsModal, assistantSession, track],
  );

  /**
   * Delete a variant - opens the confirmation modal
   */
  const handleDeleteVariant = useCallback(
    async (variantId: string) => {
      if (!assistantSession.structureData) return;

      const variant = assistantSession.structureData.variants.find(
        (v) => v.id === variantId,
      );

      track('ai_assistant_delete_variant_clicked', {
        variant_id: variantId,
        variant_name: variant?.name,
        is_generated: variant?.status === 'generated',
      });

      // Open confirmation modal
      setDeleteVariantModal({
        variantId,
        variantName: variant?.name || 'Untitled Variant',
      });
    },
    [assistantSession.structureData, track],
  );

  /**
   * Close the delete variant modal
   */
  const closeDeleteVariantModal = useCallback(() => {
    setDeleteVariantModal(undefined);
  }, []);

  /**
   * Confirm deletion of the variant
   */
  const confirmDeleteVariant = useCallback(async () => {
    if (!deleteVariantModal || !assistantSession.structureData) return;

    const { variantId } = deleteVariantModal;

    // Filter out the variant to delete
    const updatedVariants = assistantSession.structureData.variants.filter(
      (v) => v.id !== variantId,
    );

    await assistantSession.updateStructureData({
      variants: updatedVariants,
    });

    track('ai_assistant_variant_deleted', {
      variant_id: variantId,
    });

    // Close the modal
    setDeleteVariantModal(undefined);
  }, [deleteVariantModal, assistantSession, track]);

  /**
   * Open the variant origin modal for a specific variant
   */
  const openVariantOriginModal = useCallback(
    (variantId: string, defaultPrompt?: VariantPrompt) => {
      track('ai_assistant_variant_origin_modal_opened', {
        variant_id: variantId,
        has_default_prompt: !!defaultPrompt,
      });
      setVariantOriginModal({ variantId, defaultPrompt });
    },
    [track],
  );

  /**
   * Close the variant origin modal
   */
  const closeVariantOriginModal = useCallback(() => {
    setVariantOriginModal(undefined);
  }, []);

  /**
   * Handle selection from the variant origin modal and proceed with generation
   */
  const handleVariantOriginSelect = useCallback(
    async (result: VariantOriginResult) => {
      if (!variantOriginModal) return;

      const { variantId } = variantOriginModal;

      track('ai_assistant_variant_origin_selected', {
        variant_id: variantId,
        origin_type: result.originType,
        prompt: result.prompt,
        source_variant_id: result.selectedVariant?.variantId,
        source_dynamic_page_id: result.selectedVariant?.dynamicPageId,
      });

      // Close the modal first
      setVariantOriginModal(undefined);

      // Proceed with generation, passing the origin data to support copying content
      await handleGenerateVariant(variantId, result);
    },
    [variantOriginModal, track, handleGenerateVariant],
  );

  /**
   * Open settings for the dynamic page
   */
  const handleSettings = useCallback(() => {
    track('ai_assistant_settings_clicked');
    setSettingsModalOpen(true);
  }, [track]);

  /**
   * Close the settings modal
   */
  const closeSettingsModal = useCallback(() => {
    setSettingsModalOpen(false);
  }, []);

  /**
   * Update page data - updates the structureData.dynamicPage
   */
  const handleUpdatePage = useCallback(
    async (data: any, _mergeFields?: string[]) => {
      if (!assistantSession.structureData) return;

      await assistantSession.updateStructureData({
        dynamicPage: {
          ...assistantSession.structureData.dynamicPage,
          ...data,
        },
      });
    },
    [assistantSession],
  );

  /**
   * Update metadata (path, alternatePathes) - updates the structureData.dynamicPage
   */
  const handleUpdateMetadata = useCallback(
    async (metadata: { path?: string; alternatePathes?: string[] }) => {
      if (!assistantSession.structureData) return;

      await assistantSession.updateStructureData({
        dynamicPage: {
          ...assistantSession.structureData.dynamicPage,
          ...metadata,
        },
      });
    },
    [assistantSession],
  );

  /**
   * Navigate to the created dynamic page
   */
  const handleGoToPage = useCallback(() => {
    if (!selectedAccount) return;

    const slug = selectedAccount.data().slug;
    const dynamicPageId =
      assistantSession.structureData?.createdDynamicPageId ||
      assistantSession.agentSessionData?.dynamicPageId;

    if (dynamicPageId && accountId) {
      track('ai_assistant_go_to_page_clicked', {
        dynamic_page_id: dynamicPageId,
      });
      navigate(`/workspace/${slug}/pages/${dynamicPageId}`);
    }
  }, [
    selectedAccount,
    assistantSession.structureData?.createdDynamicPageId,
    assistantSession.agentSessionData?.dynamicPageId,
    accountId,
    track,
    navigate,
  ]);

  return {
    handleGenerateVariant,
    handleEditVariant,
    handleEditConstraints,
    handleDeleteVariant,
    handleSettings,
    handleGoToPage,
    generatingVariantId,
    editVariantModal,
    closeEditVariantModal,
    saveEditVariantModal,
    editConstraintsModal,
    closeEditConstraintsModal,
    saveEditConstraintsModal,
    deleteVariantModal,
    closeDeleteVariantModal,
    confirmDeleteVariant,
    variantOriginModal,
    openVariantOriginModal,
    closeVariantOriginModal,
    handleVariantOriginSelect,
    settingsModalOpen,
    closeSettingsModal,
    handleUpdatePage,
    handleUpdateMetadata,
  };
}
